import { useState, useCallback, useRef } from "react";
import { dataProvider } from "data";
import { apiMethods } from "data/api.constants";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../constants/ZoomAttendance.constants";
import { formatParticipantId } from "../utils/ZoomAttendance.utils";
import { logError } from "utils/error";

/**
 * Custom hook to fetch and export session participants for a given slot.
 *
 * @param {Object} params - Hook parameters.
 * @param {string} params.slotUuid - Unique identifier for the meeting slot.
 * @returns {Object} Session participants list, total records, pagination, filters, export and refetch handlers.
 */
export const useSessionParticipants = ({
  slotUuid,
  filterValues: externalFilterValues = {},
}) => {
  const [page, setPage] = useState(
    ZOOM_ATTENDANCE_CONSTANTS.DEFAULT_PAGINATION.INITIAL_PAGE
  );
  const [pageSize, setPageSize] = useState(
    ZOOM_ATTENDANCE_CONSTANTS.DEFAULT_PAGINATION.PAGE_SIZE_DEFAULT
  );
  const [attendanceType, setAttendanceType] = useState(
    ZOOM_ATTENDANCE_CONSTANTS.PARTICIPANT_ATTENDANCE_TYPES.NO_FILTER
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const exportInFlightRef = useRef(false);

  const params = {
    slot_uuid: slotUuid,
    page,
    page_size: pageSize,
    attendance_type: attendanceType,
    ...filterValues,
    ...externalFilterValues,
  };
  if (searchQuery) {
    params.customer_name = searchQuery;
  }

  const {
    data: queryData,
    isLoading: loading,
    isFetching,
    refetch: refetchQuery,
  } = useCustomQuery({
    url: ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.GET_SESSION_PARTICIPANTS,
    params,
    queryKey: [
      ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.GET_SESSION_PARTICIPANTS,
      slotUuid,
      page,
      pageSize,
      attendanceType,
      searchQuery,
      filterValues,
      externalFilterValues,
    ],
    staleTime: ZOOM_ATTENDANCE_CONSTANTS.QUERY_CACHE_TIME_MS,
    enabled: Boolean(slotUuid),
    onError: (err) => {
      logError({
        error: err,
        when: ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.FETCH_PARTICIPANTS_ERROR_WHEN,
        occuredAt: ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.HOOK_OCCURRENCE,
      });
    },
  });

  let data = [];
  let total = ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;
  let totalPages = ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ONE;

  if (queryData) {
    const rawParticipants =
      queryData.participants || queryData.data?.participants || [];
    data = rawParticipants.map((p, idx) => ({
      ...p,
      id: formatParticipantId(p, idx),
    }));
    total =
      queryData.total ||
      queryData.data?.total ||
      ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;
    totalPages =
      queryData.total_pages ||
      queryData.data?.total_pages ||
      ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ONE;
  }

  const exportParticipants = useCallback(
    async ({ notify, filterValues: activeFilterValues = {} } = {}) => {
      if (!slotUuid || exportInFlightRef.current) return [];
      exportInFlightRef.current = true;
      try {
        const exportParams = {
          slot_uuid: slotUuid,
          page: ZOOM_ATTENDANCE_CONSTANTS.DEFAULT_PAGINATION.PAGE_ALL,
          attendance_type: attendanceType,
          ...filterValues,
          ...activeFilterValues,
        };

        if (activeFilterValues.name || activeFilterValues.customer_name) {
          exportParams.customer_name =
            activeFilterValues.customer_name || activeFilterValues.name;
        }
        if (activeFilterValues.email || activeFilterValues.customer_email) {
          exportParams.customer_email =
            activeFilterValues.customer_email || activeFilterValues.email;
        }
        if (activeFilterValues.phone || activeFilterValues.customer_phone) {
          exportParams.customer_phone =
            activeFilterValues.customer_phone || activeFilterValues.phone;
        }

        const response = await dataProvider.custom_request(
          ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.GET_SESSION_PARTICIPANTS,
          apiMethods.GET,
          exportParams
        );
        const list = response?.data?.participants || response?.data || [];
        return list.map((p, idx) => ({
          ...p,
          id: formatParticipantId(p, idx),
        }));
      } catch (err) {
        logError({
          error: err,
          when: ZOOM_ATTENDANCE_CONSTANTS.MESSAGES
            .EXPORT_PARTICIPANTS_ERROR_WHEN,
          occuredAt: ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.HOOK_OCCURRENCE,
        });
        if (notify) {
          notify(ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.EXPORT_FAILURE, "error");
        }
        return [];
      } finally {
        exportInFlightRef.current = false;
      }
    },
    [slotUuid, attendanceType, filterValues]
  );

  return {
    data,
    total,
    totalPages,
    loading,
    isFetching: Boolean(isFetching),
    page,
    setPage,
    pageSize,
    setPageSize,
    attendanceType,
    setAttendanceType,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    exportParticipants,
    refetch: refetchQuery,
  };
};
